import { forwardRef, useState, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../utils';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const sizeStyles = {
  sm: 'h-10',
  md: 'h-12',
  lg: 'h-14',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      size = 'md',
      className,
      id,
      disabled,
      value,
      defaultValue,
      placeholder,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const hasError = Boolean(error);
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    // Inline-style fallback for the error state. Tailwind + CSS vars already
    // drive the red via `text-tkf-fg-error` / `border-tkf-line-error`, but in
    // edge cases (dev-server cache, extension that blocks custom properties)
    // the semantic class can silently no-op. The `style` override guarantees
    // the visual cue in those cases — and is a no-op whenever the class works.
    const errorLabelStyle = hasError && !disabled ? { color: '#EF5350' } : undefined;
    const errorInputStyle = hasError ? { borderColor: '#EF5350' } : undefined;
    const errorMessageStyle = { color: '#EF5350' };

    return (
      <div className="w-full">
        <div className="relative">
          {/* Floating Label - Always positioned above border */}
          {label && (
            <label
              htmlFor={inputId}
              style={{
                ...errorLabelStyle,
                // Top half transparent (shows page bg), bottom half matches the input's
                // own surface. Disabled inputs use surface-muted (gray), enabled use
                // surface (white) — must match or the notch bleeds through the border.
                background: disabled
                  ? 'linear-gradient(to bottom, transparent 45%, var(--tkf-surface-muted) 45%)'
                  : 'linear-gradient(to bottom, transparent 45%, var(--tkf-surface) 45%)',
              }}
              className={cn(
                'absolute left-3 top-0 -translate-y-1/2 pointer-events-none',
                'px-1 text-sm font-medium transition-colors duration-200',
                leftIcon && 'left-10',
                hasError
                  ? 'text-tkf-fg-error'
                  : isFocused
                    ? 'text-primary-500'
                    : 'text-tkf-fg-muted',
                disabled && 'text-tkf-fg-disabled'
              )}
            >
              {label}
            </label>
          )}

          {leftIcon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-tkf-fg-subtle">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            style={errorInputStyle}
            className={cn(
              'block w-full rounded-lg border bg-tkf-surface px-3 text-tkf-fg transition-colors duration-200',
              'focus:outline-none focus:ring-1',
              'disabled:cursor-not-allowed disabled:bg-tkf-surface-muted disabled:text-tkf-fg-disabled',
              'placeholder:text-tkf-fg-subtle',
              '[&::-ms-reveal]:hidden [&::-ms-clear]:hidden',
              hasError
                ? 'border-tkf-line-error focus:border-tkf-line-error focus:ring-tkf-line-error'
                : 'border-tkf-line-medium focus:border-primary-500 focus:ring-primary-500',
              sizeStyles[size],
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            {...props}
          />

          {rightIcon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-tkf-fg-subtle">
              {rightIcon}
            </div>
          )}
        </div>

        {error && (
          <p
            id={`${inputId}-error`}
            style={errorMessageStyle}
            className="mt-1 text-sm text-tkf-fg-error"
          >
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${inputId}-hint`} className="mt-1 text-sm text-tkf-fg-muted">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
